import { useCallback, useEffect, useRef, useState } from "react";

import { emitAccountPref, onAccountPref } from "./accountPrefBus";

// web-rnw twin of ./useAccountPref.ts (see that file's header): localStorage
// instead of AsyncStorage, read synchronously.

const read = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

export function useAccountPref<T extends string | number>(
  storageKey: string | null,
  parse: (raw: string) => T | null,
  fallback: T,
): [T, (value: T) => void] {
  const load = (key: string | null): T => {
    const raw = key ? read(key) : null;
    return (raw === null ? null : parse(raw)) ?? fallback;
  };
  // Read in the initializer: the first frame already shows the stored value.
  const [value, setValue] = useState<T>(() => load(storageKey));
  const loadedKey = useRef(storageKey);

  useEffect(() => {
    // Another account signed in: read its value (the first key is already read).
    if (loadedKey.current !== storageKey) {
      loadedKey.current = storageKey;
      setValue(load(storageKey));
    }
    if (!storageKey) return;
    return onAccountPref(storageKey, (next) => {
      const v = parse(next);
      if (v !== null) setValue(v);
    });
    // `parse` and `fallback` are module constants at every caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const set = useCallback(
    (v: T) => {
      setValue(v);
      if (!storageKey) return;
      try {
        localStorage.setItem(storageKey, String(v));
      } catch {
        // quota exceeded / private mode: non-fatal, the choice holds this session.
      }
      emitAccountPref(storageKey, String(v));
    },
    [storageKey],
  );

  return [value, set];
}
